/**
 * HeapAnalytics class for easy integration with web applications
 * Fails silently with console messages - never throws or rejects
 */
class HeapAnalytics {
  /**
   * Initialize Heap Analytics
   * @param {Object} config Configuration options
   * @param {string} config.appId Your Heap App ID (required)
   * @param {boolean} [config.enabled=true] Whether Heap tracking is enabled
   * @param {boolean} [config.development=false] Whether to use development mode
   * @param {boolean} [config.disableTextCapture=true] Whether to disable automatic text capture
   * @param {string} [config.applicationName] Application name attached to user properties
   * @param {string} [config.environment] Deployment environment attached to user properties
   */
  constructor({
    appId,
    enabled = true,
    development = false,
    disableTextCapture = true,
    applicationName,
    environment
  } = {}) {
    this.appId = appId;
    this.enabled = enabled;
    this.development = development;
    this.disableTextCapture = disableTextCapture;
    this.applicationName = applicationName;
    this.environment = environment;
    this._ready = null;
    this._pendingCalls = [];

    // Grab heap from window scope if it already exists
    if (typeof window !== 'undefined' && window.heap) {
      this.heap = window.heap;
      this.appId = window.heap.appid;
      this.development = window.heap.config?.development;
      this.disableTextCapture = window.heap.config?.disableTextCapture;
      // Already loaded — resolve immediately so _whenReady calls proceed
      this._ready = Promise.resolve();
      console.info('Heap Analytics: Attached to existing window.heap instance');
    } else {
      this.heap = null;
    }

    return this;
  }

  /**
   * Initialize and load the Heap Analytics script
   * @returns {Promise} Always resolves, never rejects
   */
  initialize() {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      console.warn('Heap Analytics: Not in a browser environment');
      this._ready = Promise.resolve();
      this._flushPendingCalls();
      return this._ready;
    }

    if (this._ready) {
      console.warn('Heap Analytics: Already initialized');
      return this._ready;
    }

    if (!this.enabled) {
      this._ready = Promise.resolve();
      this._flushPendingCalls();
      return this._ready;
    }

    if (!this.appId) {
      console.warn('Heap Analytics: No App ID provided, skipping initialization');
      this._ready = Promise.resolve();
      this._flushPendingCalls();
      return this._ready;
    }

    this._ready = new Promise((resolve) => {
      try {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.src = `https://cdn.heapanalytics.com/js/heap-${this.appId}.js`;

        const timeout = setTimeout(() => {
          console.warn('Heap Analytics: Script load timed out');
          resolve();
        }, 10000);

        script.onload = () => {
          clearTimeout(timeout);
          resolve();
        };

        script.onerror = () => {
          clearTimeout(timeout);
          console.warn('Heap Analytics: Failed to load script');
          resolve();
        };

        window.heap = window.heap || [];
        window.heap.appid = this.appId;
        window.heap.config = {
          disableTextCapture: this.disableTextCapture,
          development: this.development
        };

        const firstScript = document.getElementsByTagName('script')[0];
        if (firstScript && firstScript.parentNode) {
          firstScript.parentNode.insertBefore(script, firstScript);
        } else {
          document.head.appendChild(script);
        }
      } catch (e) {
        console.warn('Heap Analytics: Initialization error -', e.message);
        resolve();
      }
    });

    this._flushPendingCalls();
    return this._ready;
  }

  _flushPendingCalls() {
    const pendingCalls = this._pendingCalls;
    this._pendingCalls = [];
    pendingCalls.forEach(({ method, fn }) => {
      this._whenReady(method, fn);
    });
  }

  /**
   * Safely execute a Heap command after initialization
   * @param {string} method Method name for logging
   * @param {Function} fn Function to execute
   * @private
   */
  _whenReady(method, fn) {
    if (!this._ready) {
      this._pendingCalls.push({ method, fn });
      return;
    }

    this._ready.then(() => {
      try {
        if (!this.enabled || !this.appId) {
          return;
        }

        // Refresh reference from window in case it was loaded after construction
        if (typeof window !== 'undefined' && window.heap) {
          this.heap = window.heap;
        }

        if (this.heap && typeof this.heap[method] === 'function') {
          fn();
        } else {
          console.warn(`Heap Analytics: ${method} not available`);
        }
      } catch (e) {
        console.warn(`Heap Analytics: Error calling ${method} -`, e.message);
      }
    });
  }

  /**
   * Identify a user via the Heap Server-Side API
   * Resolves with true on success, false on failure. Never rejects.
   * Also calls client-side identify so the session is linked immediately.
   * @param {string} userId Unique identifier for the user
   * @param {Object} [properties={}] Additional user properties
   * @returns {Promise<boolean>} Whether the identification was successful
   */
  async identifyUser(userId, properties = {}) {
    if (!userId) {
      console.warn('Heap Analytics: No user ID provided, skipping identify');
      return false;
    }

    // Also call client-side identify so the current session is linked
    this._whenReady('identify', () => {
      window.heap.identify(userId);
      if (Object.keys(properties).length > 0) {
        if (typeof window.heap.addUserProperties === 'function') {
          window.heap.addUserProperties(properties);
        }
        console.info("Client side user identified");
      }
    });
  }

  /**
   * Track a custom event
   * @param {string} eventName Name of the event
   * @param {Object} [properties={}] Event properties
   */
  trackEvent(eventName, properties = {}) {
    if (!eventName) {
      console.warn('Heap Analytics: No event name provided, skipping track');
      return;
    }

    this._whenReady('track', () => {
      window.heap.track(eventName, properties);
    });
  }

  /**
   * Add properties to the current user
   * @param {Object} properties User properties to add/update
   */
  addUserProperties(properties = {}) {
    if (Object.keys(properties).length === 0) return;

    this._whenReady('addUserProperties', () => {
      window.heap.addUserProperties(properties);
    });
  }

  /**
   * Add properties that will be included with all subsequent events
   * @param {Object} [properties={}] Properties to add to all events
   */
  addEventProperties(properties = {}) {
    this._whenReady('addEventProperties', () => {
      window.heap.addEventProperties(properties);
    });
  }

  /**
   * Clear all event properties
   */
  clearEventProperties() {
    this._whenReady('clearEventProperties', () => {
      window.heap.clearEventProperties();
    });
  }

  /**
   * Reset the current user's identity
   */
  resetIdentity() {
    this._whenReady('resetIdentity', () => {
      window.heap.resetIdentity();
    });
  }
}

export default HeapAnalytics;